/**
 * "Share your page" — the partner's FinderlyFix-hosted page
 * (`finderlyfix.com/p/{slug}`, FR-018, migration 063) as something to hand
 * out: the address, Copy, Open, and a QR code for flyers.
 *
 * Owner feedback (2026-09-27): the Dashboard's share panel read as either "a
 * custom widget" or "a link to my stats". So there are two panels, side by
 * side and named for what they share: "Share your widget page" (this — public,
 * for homeowners, no numbers) and "Share your stats" (ShareLinks.tsx — private,
 * numbers only, no widget).
 *
 * How the page works (server/src/routes/partner/hosted-page.ts,
 * services/hosted-page.ts):
 *   * up to 3 pages per organisation (migration 085), each backed by one LIVE
 *     site key (default: the oldest live site). Created and edited on Sites &
 *     keys (HostedPagePanel); the address is fixed once created, so this panel
 *     never creates one — it sends the partner to the form where they choose
 *     it. Each page is listed with its own link, Copy, Open and QR code;
 *   * it is public (`served`) when it is switched on, its key is live and
 *     enabled, and the organisation is not suspended. No operator approval is
 *     needed: a PENDING organisation's page is live, only search engines are
 *     told not to index it until approval (org/status.tsx);
 *   * anything else is a 404 on finderlyfix.com, so this panel shows no Open
 *     link or QR code then — it says why and where to fix it.
 *
 * Owner and admin only, like the API (roles.ts `hostedPage`).
 */
import type { ReactNode } from 'react';
import type { HostedPage, HostedPageList } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { CopyButton, ErrorNotice, Loading } from '../ui/kit';
import { useQuery } from '../ui/hooks';
import { QrCode, QrDownload } from '../ui/QrCode';
import { HOSTED_PAGES_MAX, loadHostedPages } from './HostedPagePanel';

/** Sites & keys, scrolled to the page form (Sites.tsx reads the hash): the older link, still honoured. */
export const WIDGET_PAGE_ANCHOR = 'widget-page';
export const SITES_PAGE_PATH = `/sites#${WIDGET_PAGE_ANCHOR}`;
/** Your page (`/pages`, screens/Pages.tsx): where every "see / set up / edit your page" link goes. */
export const WIDGET_PAGE_PATH = '/pages';

/** The address with Copy and Open, and its QR code. Only for a page that is serving. `children` sit under the buttons. */
export function WidgetPageLink({ page, children }: { page: Pick<HostedPage, 'url' | 'slug'>; children?: ReactNode }) {
  return (
    <div className="widget-page-link">
      <div className="stack-sm" style={{ minWidth: 0 }}>
        <div className="keyline" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <code className="mono wrap-anywhere">{page.url}</code>
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <CopyButton text={page.url} label="Copy link" />
          <a className="btn btn-sm" href={page.url} target="_blank" rel="noopener noreferrer">
            Open page<span className="visually-hidden"> (opens in a new tab)</span>
          </a>
          <QrDownload text={page.url} filename={`${page.slug}-qr.svg`} />
        </div>
        {children}
      </div>
      <QrCode text={page.url} label={`QR code for ${page.url}`} />
    </div>
  );
}

/** One page in the share list: its link and QR code when it is serving, otherwise why not. */
function PageRow({ page }: { page: HostedPage }) {
  const { org } = useSession();
  if (!page.enabled) {
    return (
      <div className="row-between">
        <p className="small" style={{ margin: 0, flex: '1 1 260px' }}>
          <span className="mono wrap-anywhere">{page.url}</span> is switched off, so there’s no link to share.
        </p>
        <Link className="btn btn-sm" to={WIDGET_PAGE_PATH}>
          Turn it on
        </Link>
      </div>
    );
  }
  if (!page.served) {
    return (
      <div className="notice warn" role="status">
        <div className="stack-sm" style={{ gap: 2 }}>
          <strong>
            <span className="wrap-anywhere">{page.url}</span> isn’t live, so there’s no link to share yet.
          </strong>
          <span>
            {org?.status === 'suspended'
              ? 'It’s offline while your organisation is suspended.'
              : 'It goes live once its live site key is enabled on Sites & keys.'}{' '}
            <Link to={SITES_PAGE_PATH}>Open Sites &amp; keys</Link>
          </span>
        </div>
      </div>
    );
  }
  return (
    <WidgetPageLink page={page}>
      {org?.status === 'pending' ? (
        <p className="small muted" style={{ margin: 0 }}>
          It’s live now. Search engines won’t list it until FinderlyFix approves your organisation.
        </p>
      ) : null}
    </WidgetPageLink>
  );
}

function Body({ list }: { list: HostedPageList }) {
  const pages = list.items;
  const limit = list.limit ?? HOSTED_PAGES_MAX;
  if (!pages.length) {
    return (
      <div className="row-between">
        <p className="small" style={{ margin: 0, flex: '1 1 260px' }}>
          You don’t have a page yet. We host up to {limit} at <span className="mono">finderlyfix.com/p/…</span> — handy for a text, an
          email signature or a flyer, even if the widget is on your own site too.
        </p>
        <Link className="btn btn-sm" to={WIDGET_PAGE_PATH}>
          Set up your page
        </Link>
      </div>
    );
  }
  return (
    <div className="stack">
      <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Your pages">
        {pages.map((page) => (
          <li key={page.slug}>
            <PageRow page={page} />
          </li>
        ))}
      </ul>
      <p className="small muted" style={{ margin: 0 }}>
        Print a QR code on flyers or van signs. <Link to={WIDGET_PAGE_PATH}>Edit your page</Link>
        {pages.length < limit ? ', or add one (per brand, service area or campaign)' : ''}.
      </p>
    </div>
  );
}

export function WidgetPageShare() {
  const { api, role } = useSession();
  const allowed = can(role, 'hostedPage');
  const list = useQuery(() => loadHostedPages(api), [], { enabled: allowed });
  if (!allowed) return null;
  return (
    <section className="panel" aria-labelledby="widget-page-share-title">
      <div className="panel-head">
        <div className="stack-sm" style={{ gap: 2 }}>
          <h2 id="widget-page-share-title">Share your page</h2>
          <span className="small muted">Your public page for homeowners: your details, the photo diagnosis and a quote form. It shows no stats.</span>
        </div>
      </div>
      <div className="panel-body">
        {list.loading ? (
          <Loading label="Checking for your pages" />
        ) : list.error ? (
          <ErrorNotice error={list.error} />
        ) : (
          <Body list={list.data ?? { items: [], limit: HOSTED_PAGES_MAX, used: 0 }} />
        )}
      </div>
    </section>
  );
}
